/* eslint-disable no-nested-ternary */
import { RefObject, useEffect, useState } from 'react';
import { Stack, Typography, LinearProgress } from '@mui/material';
import { Tour } from '@/_models/Tour';
import AdminAPI from '@/api/TreedisAdminDAO';
import { renderIcon } from '../util';
import { ratingBoardStyle, menuButtonStyle } from './styles';

export const RatingBoard = ({
  inputClientRect,
  actionPathFinished,
  loadingFinished,
  setLoadingFinished,
  tour,
  target,
  ratingEnabled,
  beforeVoteText,
  afterVoteText,
  rtl,
  ratingBoardRef,
  ratingClientRect,
  setRatingClientRect,
}: {
  inputClientRect: DOMRect;
  actionPathFinished: boolean;
  loadingFinished: boolean;
  setLoadingFinished: (val: boolean) => void;
  tour: Tour;
  target: any;
  ratingEnabled: boolean;
  beforeVoteText: string;
  afterVoteText: string;
  rtl: boolean;
  ratingBoardRef: RefObject<HTMLDivElement>;
  ratingClientRect: DOMRect;
  setRatingClientRect: any;
}) => {
  const [isClosed, setIsClosed] = useState(false);
  const [ratings, setRatings] = useState([
    { value: 1, icon: '/navigation/emoji_1.svg', iconHover: '/navigation/emoji_1_hover.svg', isHovered: false },
    { value: 2, icon: '/navigation/emoji_2.svg', iconHover: '/navigation/emoji_2_hover.svg', isHovered: false },
    { value: 3, icon: '/navigation/emoji_3.svg', iconHover: '/navigation/emoji_3_hover.svg', isHovered: false },
    { value: 4, icon: '/navigation/emoji_4.svg', iconHover: '/navigation/emoji_4_hover.svg', isHovered: false },
    { value: 5, icon: '/navigation/emoji_5.svg', iconHover: '/navigation/emoji_5_hover.svg', isHovered: false },
  ]);
  const [progress, setProgress] = useState(0);
  const [isHidden, setIsHidden] = useState(false);
  const [display, setDisplay] = useState('flex');
  const [selectedRating, setSelectedRating] = useState(null);
  const [touchStart, setTouchStart] = useState(null);

  const handleMouseHover = (rating) => {
    setRatings((prevRatings) => prevRatings.map((prevRating) => (prevRating.value === rating.value ? { ...prevRating, isHovered: true } : prevRating)));
  };

  const handleMouseLeave = (rating) => {
    setRatings((prevRatings) => prevRatings.map((prevRating) => (prevRating.value === rating.value ? { ...prevRating, isHovered: false } : prevRating)));
  };

  const handleSelectRating = async (rating) => {
    if (selectedRating) return;
    setSelectedRating(rating);

    const dataToSave = {
      rating: rating.value,
      tourId: tour.id,
      targetId: target.id,
      targetTitle: target.title,
    };
    await AdminAPI.saveNavigationRating(dataToSave);
  };

  useEffect(() => {
    setRatingClientRect(ratingBoardRef?.current?.getBoundingClientRect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosed]);

  useEffect(() => {
    let timer;

    // Start the timer only if actionPathFinished is true
    if (actionPathFinished && !loadingFinished) {
      timer = setInterval(() => {
        setProgress((oldProgress) => {
          const newProgress = oldProgress + 3.125;
          if (newProgress >= 100) {
            clearInterval(timer);
            return 100; // Cap the progress at 100
          }
          return newProgress;
        });
      }, 250);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPathFinished]);

  useEffect(() => {
    if (progress === 100) {
      setLoadingFinished(true); // Indicate loading has finished
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  // Function to detect swipe direction
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const distance = touchStart - e.targetTouches[0].clientY;
    const isSwipeDown = distance < 0;
    const isSwipeUp = distance > 0;

    if (isSwipeDown) {
      // Handle swipe down
      setIsClosed(true);
    }
    if (isSwipeUp) {
      // Handle swipe up
      setIsClosed(false);
    }
  };

  return (
    <>
      <Stack
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        alignItems="center"
        justifyContent="center"
        sx={{
          ...menuButtonStyle,
          display: { xs: isClosed ? 'none' : 'flex', lg: 'none' },
          bottom: ratingClientRect?.height + 34,
          animation: isClosed ? 'slide-down-mobile-button 0.5s ease-out forwards' : 'slide-up-mobile-button 0.5s ease-out forwards',
          '@keyframes slide-down-mobile-button': {
            '0%': {
              bottom: ratingClientRect?.height + 34,
            },
            '100%': {
              bottom: '-100%',
            },
          },
          '@keyframes slide-up-mobile-button': {
            '0%': {
              bottom: 0,
            },
            '100%': {
              bottom: ratingClientRect?.height + 34,
            },
          },
        }}
      >
        <Stack
          sx={{
            background: '#fff',
            width: '47px',
            height: '6px',
            borderRadius: '26px',
          }}
        />
      </Stack>
      <Stack
        ref={ratingBoardRef}
        sx={{
          ...ratingBoardStyle,
          top: { xs: 'unset', lg: inputClientRect?.top + inputClientRect?.height },
          animation: {
            xs: isClosed ? 'fade-out-mobile 0.5s ease-out forwards' : 'mobile-slide-up 0.5s ease-out forwards',
            lg: isHidden ? 'fade-out 0.5s ease-out forwards' : 'slide-down-fade-in 0.5s ease-out forwards',
          },
          display,
        }}
        alignItems="center"
        justifyContent="center"
        onAnimationEnd={(event) => {
          if (event.animationName === 'fade-out') {
            setRatingClientRect({ height: 0 });
            setLoadingFinished(true);
            setDisplay('none');
          }
        }}
      >
        <Stack width="100%" mb="20px">
          <LinearProgress variant="determinate" value={progress} />
        </Stack>
        <Stack
          width="50px"
          height="50px"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          sx={{ background: 'transparent', borderRadius: '9px', p: 0, border: '1px solid #E8E8E8', mb: '20px', display: { xs: 'flex', lg: 'none' } }}
        >
          {renderIcon(target, '50px', '50px')}
        </Stack>
        <Typography fontSize={{ xs: '18px', lg: '24px' }} fontWeight={500}>
          {rtl ? 'הגעת ליעד!' : 'You arrived to destination!'}
        </Typography>
        {ratingEnabled && (
          <Typography fontSize={{ xs: '16px', lg: '24px' }} fontWeight={400}>
            {!selectedRating
              ? beforeVoteText || rtl
                ? 'האם אהבת את הניווט?'
                : 'Did you like the navigation?'
              : afterVoteText || rtl
              ? 'תודה רבה על הדירוג!'
              : 'Thank you for your vote!'}
          </Typography>
        )}
        {ratingEnabled && (
          <Stack direction="row" gap={2} mt="6px">
            {ratings.map((rating) => {
              return (
                <img
                  onMouseEnter={() => handleMouseHover(rating)}
                  onMouseLeave={() => handleMouseLeave(rating)}
                  onClick={() => {
                    handleSelectRating(rating);
                    setTimeout(() => {
                      setIsHidden(true);
                    }, 2000);
                  }}
                  key={rating.value}
                  width="41px"
                  height="41px"
                  src={rating.isHovered || rating.value === selectedRating?.value ? rating.iconHover : rating.icon}
                  alt={`rating-${rating.value}`}
                  style={{ cursor: 'pointer' }}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </>
  );
};
